import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomeHero() {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    fetch(`${API}/hero/slides`)
      .then((r) => r.json())
      .then((rows) => setSlides(Array.isArray(rows) ? rows : []))
      .catch(() => setSlides([]));
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length) {
    return <div className="w-full h-[70vh] bg-gradient-to-br from-zinc-900 to-zinc-800" />;
  }

  const slide = slides[idx];
  const hasHeadline = !!(slide.headline && slide.headline.trim());
  const hasEyebrow = !!(slide.eyebrow && slide.eyebrow.trim());
  const hasSub = !!(slide.subheadline && slide.subheadline.trim());
  const hasPrimaryCta = !!(slide.cta_text && slide.cta_text.trim());
  const hasSecondaryCta = !!(slide.secondary_cta_text && slide.secondary_cta_text.trim());
  const hasText = hasHeadline || hasEyebrow || hasSub || hasPrimaryCta || hasSecondaryCta;
  // Pure-image slide → no dark overlay, no accent, no content chrome
  const overlay = hasText ? (typeof slide.overlay_opacity === "number" ? slide.overlay_opacity : 0.55) : 0;
  const align = slide.text_align || "left";
  const textCls = align === "center" ? "text-center items-center" : align === "right" ? "text-right items-end" : "text-left items-start";

  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="relative w-full h-[85vh] min-h-[520px] max-h-[820px]">
        {/* Background — video (if provided) else image */}
        {slides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
          >
            {s.video_url ? (
              <video
                src={s.video_url}
                autoPlay
                muted
                loop
                playsInline
                preload={i === idx ? "auto" : "none"}
                poster={s.image_url || undefined}
                className="w-full h-full object-cover"
                data-testid={`hero-video-${i}`}
              />
            ) : (
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `url("${s.image_url}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            )}
          </div>
        ))}
        {/* Overlay — only when text/CTA is present */}
        {overlay > 0 && (
          <div className="absolute inset-0" style={{ opacity: overlay, backgroundColor: slide.overlay_color || "#000000" }} data-testid="hero-overlay" />
        )}
        {/* Diagonal accent — only when text/CTA is present */}
        {hasText && (
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(120deg, rgba(255,107,0,0.15) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.4) 100%)" }} />
        )}

        {/* Content */}
        {hasText && (
          <div className={`relative z-10 h-full flex flex-col justify-center max-w-7xl mx-auto px-6 lg:px-8 ${textCls}`}>
            <div className={`max-w-2xl flex flex-col ${textCls}`}>
              {hasEyebrow && (
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[#FF6B00] mb-4 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20" data-testid="hero-eyebrow">
                  <Sparkles size={12} /> {slide.eyebrow}
                </div>
              )}
              {hasHeadline && (
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white whitespace-pre-line" data-testid="hero-headline">
                  {slide.headline}
                </h1>
              )}
              {hasSub && (
                <p className={`${hasHeadline ? "mt-5" : ""} text-lg md:text-xl text-white/85 leading-relaxed max-w-xl`} data-testid="hero-subheadline">
                  {slide.subheadline}
                </p>
              )}
              {(hasPrimaryCta || hasSecondaryCta) && (
                <div className={`${hasHeadline || hasSub ? "mt-8" : ""} flex flex-wrap gap-3`}>
                  {hasPrimaryCta && (
                    slide.cta_url?.startsWith("http") ? (
                      <a href={slide.cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#e65a00] text-white font-bold px-7 py-3.5 rounded-full text-sm uppercase tracking-widest transition-all" data-testid="hero-primary-cta">
                        {slide.cta_text} <ArrowRight size={14} />
                      </a>
                    ) : (
                      <Link to={slide.cta_url || "/products"} className="inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#e65a00] text-white font-bold px-7 py-3.5 rounded-full text-sm uppercase tracking-widest transition-all" data-testid="hero-primary-cta">
                        {slide.cta_text} <ArrowRight size={14} />
                      </Link>
                    )
                  )}
                  {hasSecondaryCta && (
                    slide.secondary_cta_url?.startsWith("http") ? (
                      <a href={slide.secondary_cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white font-bold px-7 py-3.5 rounded-full text-sm uppercase tracking-widest backdrop-blur-sm transition-all" data-testid="hero-secondary-cta">
                        {slide.secondary_cta_text}
                      </a>
                    ) : (
                      <Link to={slide.secondary_cta_url} className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white font-bold px-7 py-3.5 rounded-full text-sm uppercase tracking-widest backdrop-blur-sm transition-all" data-testid="hero-secondary-cta">
                        {slide.secondary_cta_text}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Slide indicators */}
        {slides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="hero-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-[#FF6B00]" : "w-4 bg-white/40 hover:bg-white/70"}`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Stats strip — Shivam Graphics style */}
      <div className="bg-zinc-900 text-white py-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat n="20,000+" label="Happy Clients" />
          <Stat n="10L+" label="Products Delivered" />
          <Stat n="6+ Yrs" label="In Business" />
          <Stat n="4.9★" label="Google Rating" />
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl font-black text-[#FF6B00]">{n}</div>
      <div className="text-xs uppercase tracking-widest text-white/70">{label}</div>
    </div>
  );
}
